if (!customElements.get('academy-package-selector')) {
  customElements.define('academy-package-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.listeners = new AbortController();
      queueMicrotask(() => {
        if (!this.isConnected) return;
        try { this.initialize(); }
        catch (error) {
          console.error('Academy selector could not initialize', error);
          this.listeners.abort();
          this.observer?.disconnect();
          this.clearAttribution();
          this.teardownComparison();
          this.root?.querySelectorAll('input[name="id"]').forEach((input) => { input.disabled = true; input.value = ''; });
          this.root?.querySelectorAll('[data-academy-fallback]').forEach((fallback) => { fallback.hidden = false; fallback.querySelectorAll('select').forEach((select) => { select.disabled = false; }); });
          this.setButtons(false, window.variantStrings.addToCart);
        }
      });
    }

    disconnectedCallback() {
      this.connected = false;
      this.listeners?.abort();
      this.observer?.disconnect();
      this.clearAttribution();
      this.teardownComparison();
      this.closeSizeGuide(false);
    }

    initialize() {
      this.root = this.closest('product-info');
      this.config = JSON.parse(this.querySelector('[data-academy-config]').textContent);
      this.model = window.AcademyVariants;
      this.packages = this.model.prepare(this.config.packages, this.config.names);
      this.form = this.root.querySelector('product-form form');
      this.purchase = this.form.closest('product-form');
      this.formatMoney = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      this.formatWholeMoney = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
      const signal = this.listeners.signal;
      this.drawChoices();
      this.initializeComparison();
      this.addEventListener('change', (event) => this.change(event), { signal });
      this.purchase.addEventListener('product-form:attempt', (event) => this.trackCart('attempt', event), { signal });
      this.purchase.addEventListener('product-form:success', (event) => this.trackCart('success', event), { signal });
      this.purchase.addEventListener('product-form:error', (event) => this.trackCart('error', event), { signal });
      this.purchase.addEventListener('product-form:complete', () => this.update(), { signal });
      this.form.addEventListener('submit', (event) => {
        const variant = this.model.resolve(this.currentPackage(), this.selection);
        if (!variant?.available || String(variant.id) !== this.form.querySelector('input[name="id"]').value) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.update();
          if (this.sizeRequired) this.showSizeError();
        } else {
          this.updateAttribution(this.currentPackage(), variant);
        }
      }, { capture: true, signal });
      this.dialog = this.root.querySelector('[data-academy-size-dialog]');
      this.dialog.classList.remove('show');
      this.dialog.inert = true;
      this.dialog.setAttribute('aria-hidden', 'true');
      this.querySelector('[data-academy-size-guide]').addEventListener('click', () => this.openSizeGuide(), { signal });
      this.dialog.querySelector('.product-media-modal__toggle').addEventListener('click', () => this.closeSizeGuide(), { signal });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeSizeGuide(); }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.dialog.contains(event.target) && !event.target.closest('[data-academy-size-guide]')) this.closeSizeGuide();
      }, { signal });
      this.sticky = this.root.querySelector('[data-academy-sticky]');
      if (this.sticky && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => this.sticky.classList.toggle('academy-selector__sticky-hidden', entry.isIntersecting));
        this.observer.observe(this.purchase);
      }
      window.addEventListener('popstate', () => this.restore(), { signal });
      this.restore();
      this.root.querySelectorAll('[data-academy-fallback]').forEach((fallback) => { fallback.hidden = true; fallback.querySelectorAll('select').forEach((select) => { select.disabled = true; }); });
      this.setAttribute('data-ready', '');
      customElements.whenDefined('media-gallery').then(() => {
        if (this.isConnected) { this.mediaTarget = null; this.updateMedia(this.currentPackage()); }
      });
    }

    restore() {
      const params = new URLSearchParams(location.search);
      this.selection = this.model.initial(this.packages, this.config.defaultPackage, params, this.config.initialVariant);
      this.clearSizeError();
      this.update();
      // Keep ordinary visits clean; normalize explicit selection links and remove stale variant IDs.
      if (['p', 'c', 'pack', 'package', 'colour', 'variant'].some((key) => params.has(key))) this.updateUrl();
    }

    updateUrl() {
      if (!this.config.updateUrl) return;
      const url = this.model.selectionUrl(location.href, this.selection, this.variant);
      if (url.href !== location.href) history.replaceState(history.state, '', url);
    }

    currentPackage() { return this.packages.find((pack) => pack.key === this.selection.package); }

    showSizeError() {
      const error = this.querySelector('[data-academy-size-error]');
      const fieldset = this.querySelector('[data-academy-size-fieldset]');
      error.hidden = false;
      error.textContent = this.config.strings.sizeRequired;
      fieldset.setAttribute('aria-describedby', error.id);
      this.querySelectorAll('[data-academy-size]').forEach((input) => input.setAttribute('aria-invalid', 'true'));
      this.querySelector('[data-academy-size-heading]').focus({ preventScroll: true });
      if (matchMedia('(max-width: 749px)').matches) {
        fieldset.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
    }

    clearSizeError() {
      const error = this.querySelector('[data-academy-size-error]');
      error.hidden = true;
      error.textContent = '';
      this.querySelector('[data-academy-size-fieldset]').removeAttribute('aria-describedby');
      this.querySelectorAll('[data-academy-size]').forEach((input) => input.removeAttribute('aria-invalid'));
    }

    openSizeGuide() {
      if (this.dialog.classList.contains('show')) return;
      this.closeComparison(false);
      this.dialog.inert = false;
      this.dialog.setAttribute('aria-hidden', 'false');
      this.dialog.classList.add('show');
      this.sizeGuideLocksScroll = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      this.restoreSizeBackground = inertOutside(this.dialog);
      trapFocus(this.dialog, this.dialog.querySelector('.product-media-modal__toggle'));
    }

    closeSizeGuide(returnFocus = true) {
      if (!this.dialog?.classList.contains('show')) return;
      this.dialog.classList.remove('show');
      if (this.sizeGuideLocksScroll) document.body.classList.remove('overflow-hidden');
      this.sizeGuideLocksScroll = false;
      this.restoreSizeBackground?.();
      this.restoreSizeBackground = null;
      removeTrapFocus(returnFocus ? this.querySelector('[data-academy-size-guide]') : null);
      this.dialog.inert = true;
      this.dialog.setAttribute('aria-hidden', 'true');
    }

    initializeComparison() {
      const fallback = this.root.querySelector('[data-academy-comparison-fallback]');
      const panel = this.root.querySelector('[data-academy-comparison-panel]');
      const trigger = this.querySelector('[data-academy-compare]');
      if (!fallback || !panel || !trigger) return;
      // A cloned Theme Editor section may contain an enhanced dialog rather than inline content.
      fallback.append(panel);
      fallback.hidden = false;
      panel.querySelector('[data-academy-comparison-close]').hidden = true;
      this.root.querySelector('[data-academy-comparison-dialog]')?.remove();
      this.comparisonFallback = fallback;
      this.comparisonPanel = panel;
      this.comparisonTrigger = trigger;
      const dialog = document.createElement('aside');
      dialog.className = 'size-chart-modal academy-comparison';
      dialog.id = `AcademyCompareDialog-${this.dataset.section}`;
      dialog.dataset.academyComparisonDialog = '';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-hidden', 'true');
      dialog.inert = true;
      dialog.setAttribute('aria-labelledby', `AcademyCompareTitle-${this.dataset.section}`);
      dialog.append(panel);
      this.root.append(dialog);
      this.comparisonDialog = dialog;
      fallback.hidden = true;
      const close = panel.querySelector('[data-academy-comparison-close]');
      close.hidden = false;
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', dialog.id);
      trigger.setAttribute('aria-expanded', 'false');
      const signal = this.listeners.signal;
      trigger.addEventListener('click', (event) => { event.preventDefault(); this.openComparison(); }, { signal });
      close.addEventListener('click', () => this.closeComparison(), { signal });
      document.addEventListener('keydown', (event) => {
        if (this.comparisonActive && event.key === 'Escape') { event.preventDefault(); this.closeComparison(); }
      }, { signal });
      document.addEventListener('focusin', (event) => {
        if (this.comparisonActive && !dialog.contains(event.target)) close.focus({ preventScroll: true });
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        this.comparisonBackdrop = this.comparisonActive && !dialog.contains(event.target);
      }, { capture: true, signal });
      document.addEventListener('click', (event) => {
        if (!this.comparisonActive || dialog.contains(event.target)) return;
        // Dismissing the modal must not also activate a purchase control underneath it.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.comparisonBackdrop) this.closeComparison();
        this.comparisonBackdrop = false;
      }, { capture: true, signal });
    }

    openComparison() {
      if (!this.comparisonDialog || this.comparisonActive) return;
      this.closeSizeGuide(false);
      this.updatePackageImage();
      this.comparisonDialog.inert = false;
      this.comparisonDialog.setAttribute('aria-hidden', 'false');
      this.comparisonDialog.classList.add('show');
      this.comparisonActive = true;
      this.comparisonLocksScroll = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      this.comparisonTrigger.setAttribute('aria-expanded', 'true');
      trapFocus(this.comparisonDialog, this.comparisonPanel.querySelector('[data-academy-comparison-close]'));
      this.comparisonDialog.scrollTop = 0;
    }

    closeComparison(returnFocus = true) {
      if (!this.comparisonActive) return;
      removeTrapFocus();
      this.comparisonActive = false;
      this.comparisonDialog.classList.remove('show');
      this.comparisonDialog.inert = true;
      this.comparisonDialog.setAttribute('aria-hidden', 'true');
      if (this.comparisonLocksScroll) document.body.classList.remove('overflow-hidden');
      this.comparisonLocksScroll = false;
      this.comparisonBackdrop = false;
      this.comparisonTrigger?.setAttribute('aria-expanded', 'false');
      if (returnFocus && this.comparisonTrigger?.isConnected) this.comparisonTrigger.focus({ preventScroll: true });
    }

    teardownComparison() {
      this.closeComparison(false);
      if (this.comparisonFallback && this.comparisonPanel) {
        this.comparisonFallback.append(this.comparisonPanel);
        this.comparisonFallback.hidden = false;
        this.comparisonPanel.querySelector('[data-academy-comparison-close]').hidden = true;
      }
      this.comparisonDialog?.remove();
      ['aria-haspopup', 'aria-controls', 'aria-expanded'].forEach((name) => this.comparisonTrigger?.removeAttribute(name));
      this.comparisonDialog = null;
    }

    drawChoices() {
      const choices = this.model.choices(this.packages);
      const colours = this.querySelector('[data-academy-colours]');
      colours.replaceChildren();
      choices.colours.forEach((colour) => {
        const label = document.createElement('label');
        label.className = 'academy-selector__colour';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `academy-colour-${this.dataset.section}`;
        input.value = colour;
        input.dataset.academyColour = '';
        input.className = 'academy-selector__radio';
        const content = document.createElement('span');
        content.className = 'academy-selector__colour-label';
        const swatch = document.createElement('span');
        swatch.className = 'academy-selector__swatch';
        swatch.dataset.colour = colour;
        swatch.setAttribute('aria-hidden', 'true');
        const name = document.createElement('span');
        name.textContent = this.colourLabel(colour);
        content.append(swatch, name);
        label.append(input, content);
        colours.append(label);
      });
      const sizes = this.querySelector('[data-academy-sizes]');
      sizes.replaceChildren();
      choices.fits.forEach((fit) => {
        const group = document.createElement('div');
        group.className = 'academy-selector__size-group';
        const heading = document.createElement('span');
        heading.className = 'academy-selector__sizes-label';
        heading.textContent = fit.label;
        const options = document.createElement('div');
        options.className = 'academy-selector__sizes';
        fit.sizes.forEach((size) => {
          const label = document.createElement('label');
          const input = document.createElement('input');
          input.className = 'academy-selector__radio';
          input.type = 'radio';
          input.name = `academy-size-${this.dataset.section}`;
          input.value = size.key;
          input.dataset.academySize = '';
          input.dataset.fit = fit.key;
          input.setAttribute('aria-label', `${fit.label} ${size.label}`);
          const text = document.createElement('span');
          text.className = 'academy-selector__size';
          text.textContent = /^(xxs|xs|s|m|l|xl|xxl|xxxl)$/.test(size.key) ? size.key.toUpperCase() : size.label;
          label.append(input, text);
          options.append(label);
        });
        group.append(heading, options);
        sizes.append(group);
      });
    }

    change(event) {
      const input = event.target;
      let action;
      if (input.matches('[data-academy-package]')) { this.selection.package = input.value; action = 'package_selected'; }
      if (input.matches('[data-academy-colour]')) { this.selection.colour = input.value; action = 'colour_selected'; }
      if (input.matches('[data-academy-size]')) {
        this.selection.fit = input.dataset.fit;
        this.selection.size = input.value;
        action = 'size_selected';
      }
      if (!action) return;
      this.update();
      this.updateUrl();
      this.announceSelection();
      this.track(action);
      if (!this.variant?.available && !this.sizeRequired) this.track('unavailable_combination');
    }

    update() {
      const pack = this.currentPackage();
      this.variant = this.model.resolve(pack, this.selection);
      const variant = this.variant;
      const available = !!variant?.available;
      const missingSize = !this.selection.fit || !this.selection.size;
      const preview = missingSize ? this.model.preview(pack, this.selection) : null;
      const displayVariant = variant || preview?.variant;
      this.displayVariant = displayVariant;
      this.sizeRequired = missingSize && !!preview?.variant?.available;
      if (!missingSize) this.clearSizeError();
      const strings = this.config.strings;
      const busy = this.purchase.hasAttribute('aria-busy');
      this.root.dataset.academySelectionValid = String(available);
      this.querySelectorAll('[data-academy-package]').forEach((input) => {
        const target = this.packages.find((item) => item.key === input.value);
        input.checked = input.value === this.selection.package;
        input.disabled = !target?.valid;
      });
      this.querySelectorAll('[data-academy-colour]').forEach((input) => {
        input.checked = input.value === this.selection.colour;
        const matches = pack?.valid ? pack.variants.filter((item) => item.colour === input.value) : [];
        this.availability(input, matches.length > 0, matches.some((item) => item.available));
      });
      this.querySelectorAll('[data-academy-size]').forEach((input) => {
        input.checked = input.dataset.fit === this.selection.fit && input.value === this.selection.size;
        const match = this.model.resolve(pack, { ...this.selection, fit: input.dataset.fit, size: input.value });
        this.availability(input, !!match, !!match?.available);
      });
      this.root.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = available ? variant.id : '';
        input.disabled = !available;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.form.querySelectorAll('input[name="product-id"]').forEach((input) => { input.value = pack?.id || ''; });
      this.updateAttribution(pack, variant);
      const buttonText = available || this.sizeRequired ? window.variantStrings.addToCart : (displayVariant ? strings.soldOut : strings.select);
      this.setButtons((!available && !this.sizeRequired) || busy, buttonText);
      const price = this.root.querySelector('[data-academy-main-price]');
      price.classList.toggle('visibility-hidden', !displayVariant);
      if (displayVariant) {
        this.copyTemplate(`[data-academy-price="${displayVariant.id}"]`, price);
        if (preview?.priceVaries) {
          price.querySelectorAll('.price__regular .price-item, .price-item--sale').forEach((item) => {
            item.textContent = strings.fromPrice.replace('[price]', item.textContent.trim());
          });
        }
      }
      const title = this.root.querySelector('[data-academy-title]');
      if (title && pack) title.textContent = `STATSports ${pack.label}`;
      this.root.querySelector('[data-academy-summary]')?.setAttribute('hidden', '');
      this.updatePackageImage();
      this.updateStackedPackages(pack);
      this.updateDetails(pack);
      this.updatePayments(pack, displayVariant, available);
      this.updateMedia(pack);
    }

    announceSelection() {
      const region = this.root.querySelector('[data-product-selection-status]');
      if (!region) return;
      const pack = this.currentPackage();
      const price = this.root.querySelector('[data-academy-main-price] .price-item--sale, [data-academy-main-price] .price__regular .price-item');
      const status = this.sizeRequired ? this.config.strings.sizeRequired :
        this.variant?.available ? region.dataset.inStock : this.config.strings.soldOut;
      region.textContent = [pack?.label, this.selection.colour, price?.textContent.trim(), status].filter(Boolean).join('. ');
    }

    updateAttribution(pack, variant) {
      if (!pack?.valid || !variant?.available) {
        this.clearAttribution();
        return;
      }
      const properties = {
        _pdp_source: 'Academy unified PDP',
        _academy_pack: pack.key,
        _academy_colour: variant.colour,
      };
      this.attributionInputs ||= new Map();
      Object.entries(properties).forEach(([key, value]) => {
        let input = this.attributionInputs.get(key);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.dataset.academyAttribution = '';
          this.attributionInputs.set(key, input);
        }
        input.value = value;
        // Reattach if an app replaced form contents, without duplicating our fields.
        if (input.parentElement !== this.form) this.form.append(input);
      });
    }

    clearAttribution() {
      this.attributionInputs?.forEach((input) => input.remove());
      this.attributionInputs?.clear();
    }

    updatePackageImage() {
      this.root.querySelectorAll('[data-academy-package-image="academy"]').forEach((image) => {
        const source = image.getAttribute(`data-colour-${this.selection.colour}`) || image.getAttribute('data-colour-black');
        if (source && image.getAttribute('src') !== source) image.setAttribute('src', source);
      });
    }

    updateStackedPackages(pack) {
      const included = this.querySelector('[data-academy-included]');
      if (!included) return;
      const panels = [...included.querySelectorAll('[data-academy-included-pack]')];
      included.hidden = !panels.some((panel) => panel.dataset.academyIncludedPack === pack?.key);
      panels.forEach((panel) => { panel.hidden = panel.dataset.academyIncludedPack !== pack?.key; });
      this.querySelectorAll('[data-academy-card-price]').forEach((card) => {
        const target = this.packages.find((item) => item.key === card.dataset.academyCardPrice);
        const exact = this.model.resolve(target, this.selection);
        let preview = this.model.preview(target, this.selection);
        // A pack without the current colour retains its own starting price for comparison.
        if (!preview.variant && target?.valid) {
          const available = target.variants.filter((variant) => variant.available);
          const variants = (available.length ? available : target.variants).slice().sort((a, b) => a.price - b.price);
          preview = { variant: variants[0], priceVaries: new Set(variants.map((variant) => variant.price)).size > 1 };
        }
        const variant = exact || preview.variant;
        card.hidden = !variant;
        if (!variant) return;
        const onSale = variant.compareAt > variant.price;
        card.querySelector('[data-academy-card-amount]').textContent = !exact && preview.priceVaries ? this.config.strings.fromPrice.replace('[price]', variant.priceText) : variant.priceText;
        card.querySelector('[data-academy-card-compare]').hidden = !onSale;
        card.querySelector('[data-academy-card-compare-amount]').textContent = variant.compareAtText;
        card.querySelector('[data-academy-card-price-label]').textContent = onSale ? this.config.strings.salePrice : this.config.strings.regularPrice;
        const saving = card.querySelector('[data-academy-card-saving]');
        saving.hidden = !onSale || !variant.available;
        saving.textContent = variant.saveText;
        card.querySelector('[data-academy-card-stock]').hidden = variant.available;
      });
    }

    availability(input, exists, available) {
      input.disabled = !available;
      input.closest('label').classList.toggle('is-unavailable', !available);
      input.closest('label').title = available ? '' : exists ? this.config.strings.soldOut : this.config.strings.unavailable;
    }

    setButtons(disabled, text) {
      this.root?.querySelectorAll('[data-academy-submit]').forEach((button) => {
        button.disabled = disabled;
        button.textContent = text;
      });
    }

    copyTemplate(selector, destination) {
      const template = this.querySelector(selector);
      if (template && destination) destination.replaceChildren(template.content.cloneNode(true));
    }

    colourLabel(colour) { return colour.charAt(0).toUpperCase() + colour.slice(1); }

    updateDetails(pack) {
      const destination = this.root.querySelector('[data-academy-details]');
      if (!destination || !pack || this.detailsPackage === pack.key) return;
      const openPanels = new Set([...destination.querySelectorAll('details[open]')].map((panel) => panel.dataset.academyDetail));
      this.copyTemplate(`[data-academy-details-template="${pack.key}"]`, destination);
      destination.querySelectorAll('details').forEach((panel) => { panel.open = openPanels.has(panel.dataset.academyDetail); });
      this.detailsPackage = pack.key;
    }

    updatePayments(pack, variant, available) {
      const termsForm = this.root.querySelector('[data-academy-installment]');
      if (termsForm) {
        termsForm.hidden = !available;
        termsForm.querySelectorAll('input[name="product-id"]').forEach((input) => { input.value = pack?.id || ''; });
      }
      this.root.querySelectorAll('[data-academy-klarna]').forEach((widget) => {
        // The displayed price preview supports messaging before a size is selected.
        widget.hidden = !variant?.available;
        const installments = Number(widget.dataset.installments);
        if (variant && installments) widget.querySelectorAll('[data-klarna-amount]').forEach((element) => {
          const paymentCents = Math.floor(variant.price / installments);
          const formatter = paymentCents % 100 === 0 ? this.formatWholeMoney : this.formatMoney;
          element.textContent = formatter.format(paymentCents / 100);
        });
      });
      const pickup = this.root.querySelector('pickup-availability');
      if (pickup && pickup.dataset.variantId !== String(variant?.id || '')) {
        pickup.dataset.variantId = available ? variant.id : '';
        if (available && typeof pickup.fetchAvailability === 'function') pickup.fetchAvailability(variant.id);
        else { pickup.removeAttribute('available'); pickup.innerHTML = ''; }
      }
    }

    updateMedia(pack) {
      if (!pack) return;
      let key = pack.key;
      if (pack.key === 'academy' && this.querySelector(`[data-academy-gallery="academy:${this.selection.colour}"]`)) key = `academy:${this.selection.colour}`;
      const template = this.querySelector(`[data-academy-gallery="${key}"]`);
      let galleryChanged = false;
      if (this.mediaKey !== key) {
        const gallery = this.root.querySelector('media-gallery');
        if (gallery && template) {
          window.pauseAllMedia?.();
          gallery.replaceWith(template.content.cloneNode(true));
          const modal = this.root.querySelector('product-modal');
          const modalTemplate = this.querySelector(`[data-academy-modal="${key}"]`);
          if (modal && modalTemplate) modal.replaceWith(modalTemplate.content.cloneNode(true));
          this.mediaKey = key;
          galleryChanged = true;
        }
      }
      // Gallery sources control PDP media; native variant images belong to cart/checkout.
      const mediaId = template?.content.querySelector('[data-media-id]')?.dataset.mediaId;
      if (mediaId && (galleryChanged || this.mediaTarget !== mediaId)) {
        // This theme has a horizontal desktop gallery without thumbnails.
        // Move the selected image to the front while keeping focus/scroll at the controls.
        this.root.querySelector('media-gallery')?.setActiveMedia?.(mediaId, true, false);
      }
      this.mediaTarget = mediaId;
    }

    payload(variant = this.variant, pack = this.currentPackage()) {
      return {
        canonical_product_id: this.config.canonicalProductId,
        product_id: pack?.id || null, variant_id: variant?.id || null, sku: variant?.sku || null,
        price: variant ? variant.price / 100 : null, currency: this.config.currency,
        package: pack?.key || null, colour: variant?.colour || this.selection.colour,
        fit: variant?.fit || this.selection.fit, size: variant?.size || this.selection.size,
      };
    }

    track(action, payload = this.payload()) {
      this.dispatchEvent(new CustomEvent(`academy:${action}`, { bubbles: true, detail: payload }));
      // Opt-in events for the analytics owner; never emit standard view_item/add_to_cart twice.
      if (this.config.analytics) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: `academy_${action}`, ...payload });
      }
    }

    trackCart(action, event) {
      const id = String(event.detail.variantId);
      const pack = this.packages.find((item) => item.variants.some((variant) => String(variant.id) === id));
      const variant = pack?.variants.find((item) => String(item.id) === id);
      this.track(`add_to_cart_${action}`, { ...this.payload(variant, pack), quantity: event.detail.quantity });
    }
  });
}
